// src/App.js
import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import ClassRecordForm from './components/ClassRecordForm';
import ClassRecordList from './components/ClassRecordList';
import StatisticsPage from './components/StatisticsPage';
import Login from './components/Login'; // Import Login component
import styles from './App.module.css';

// 提示框样式 (可以根据需要调整)
const notificationStyle = {
  position: 'fixed',
  top: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  padding: '10px 20px',
  backgroundColor: '#4CAF50', // 成功绿色
  color: 'white',
  borderRadius: '5px',
  zIndex: 1000,
  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
  textAlign: 'center',
};

const errorNotificationStyle = {
  ...notificationStyle,
  backgroundColor: '#f44336', // 错误红色
};

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL;

if (!BACKEND_BASE_URL) {
  console.error("FATAL: REACT_APP_BACKEND_URL environment variable is not set!");
}

// Helper function to format record data coming from backend/API response
const formatRecordData = (record) => {
    if (!record) return null;
    return {
        ...record,
        // 确保 record_time 是 Date 对象
        record_time: record.record_time ? new Date(record.record_time) : null,
        // 确保 fee 是数字，即使后端返回字符串
        fee: record.fee !== null && record.fee !== undefined ? parseFloat(record.fee) : null,
        // 确保 content 是数组
        content: Array.isArray(record.content) ? record.content : (record.content ? [record.content] : [])
    };
};


function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false); // Add login state
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [notification, setNotification] = useState({ message: '', type: '', visible: false });

  // Check login status on app load
  useEffect(() => {
    const loggedInStatus = localStorage.getItem('isLoggedIn');
    if (loggedInStatus === 'true') {
      setIsLoggedIn(true);
    }
  }, []);

  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
    // Optionally fetch records after successful login
    if (BACKEND_BASE_URL) {
      fetchRecords();
    } else {
      setLoading(false);
      setError("后端服务地址未配置，请检查环境变量。");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    setIsLoggedIn(false);
    setRecords([]); // Clear records on logout
    // Optionally redirect to login page if using react-router-dom
    // const navigate = useNavigate(); // Cannot call hooks conditionally, need to handle navigation differently if needed
  };


  // Note: fetchRecords, handleSaveRecord, handleDeleteRecord, handleStartEditing, handleCancelEditing, handleImportRecords remain the same.
  // They will be used within the routed components or passed down as props.

  // 显示提示函数
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type, visible: true });
    setTimeout(() => {
      setNotification({ message: '', type: '', visible: false });
    }, 2000); // 2秒后自动隐藏
  };

  const fetchRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!BACKEND_BASE_URL) {
           throw new Error("后端服务地址未配置，无法获取记录。");
      }
      const response = await fetch(`${BACKEND_BASE_URL}/records`);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();

      // 使用辅助函数格式化数据
      const formattedData = data.map(formatRecordData);

      // 假设后端返回的数据已经是按时间倒序排列的
      setRecords(formattedData);

    } catch (err) {
      console.error('获取记录失败:', err);
      setError(`获取记录失败: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRecord = async (recordData) => {
     if (isSubmitting) return;
     setIsSubmitting(true);

     const isUpdating = recordData.id != null;
     const method = isUpdating ? 'PUT' : 'POST';
     const url = isUpdating ? `${BACKEND_BASE_URL}/records/${recordData.id}` : `${BACKEND_BASE_URL}/records`;

     try {
       if (!BACKEND_BASE_URL) {
           throw new Error(`后端服务地址未配置，无法${isUpdating ? '更新' : '添加'}记录。`);
       }

       const dataToSend = { ...recordData };

       // 确保 record_time 是 Date 对象，并转换为 ISO 8601 字符串
       if (dataToSend.record_time instanceof Date && !isNaN(dataToSend.record_time.getTime())) {
            dataToSend.record_time = dataToSend.record_time.toISOString();
       } else {
            throw new Error("上课时间格式无效");
       }

       // content, fee, remarks 的格式化和 null 处理已经在 ClassRecordForm 中完成，直接发送
       // 但为了健壮性，可以在这里再次检查或转换
       dataToSend.content = Array.isArray(dataToSend.content) ? dataToSend.content : (dataToSend.content ? [dataToSend.content] : []);
        if (typeof dataToSend.fee === 'string') {
           dataToSend.fee = parseFloat(dataToSend.fee);
           if (isNaN(dataToSend.fee)) dataToSend.fee = null;
       }
        if (dataToSend.remarks === '') dataToSend.remarks = null;


       const response = await fetch(url, {
         method: method,
         headers: {
           'Content-Type': 'application/json',
         },
         body: JSON.stringify(dataToSend),
       });

       if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
       }

       // 获取后端返回的记录数据
       const resultRecord = await response.json();

       // 使用辅助函数格式化后端返回的单个记录
       const formattedResultRecord = formatRecordData(resultRecord);


       // --- 重点修改这里：根据操作类型更新本地 records 状态 ---
       if (isUpdating) {
           // 更新操作：找到并替换 records 数组中对应的记录
           setRecords(prevRecords =>
               prevRecords.map(record =>
                   record.id === formattedResultRecord.id ? formattedResultRecord : record
               )
           );
           // 清空编辑状态
           setEditingRecord(null);
       } else {
           // 添加操作：将新记录添加到 records 数组末尾
           // setRecords(prevRecords => [...prevRecords, formattedResultRecord]); // <-- 原来的代码，添加到末尾
           // 更改为：将新记录添加到 records 数组**开头**
           setRecords(prevRecords => [formattedResultRecord, ...prevRecords]); // <-- 修改后的代码，添加到开头
       }
       // --- 重点修改结束 ---

       showNotification(`记录${isUpdating ? '更新' : '添加'}成功!`, 'success');

     } catch (err) {
       console.error(`${isUpdating ? '更新' : '添加'}记录失败:`, err);
       showNotification(`${isUpdating ? '更新' : '添加'}记录失败: ${err.message}`, 'error');
     } finally {
        setIsSubmitting(false);
     }
   };

  const handleDeleteRecord = async (recordId) => {
    // 自定义模态框已处理确认，移除原生 window.confirm
    // const confirmDelete = window.confirm("确定要删除这条记录吗？");
    // if (!confirmDelete) {
    //   return;
    // }

    if (deletingId) return;
    setDeletingId(recordId);

    try {
        if (!BACKEND_BASE_URL) {
            throw new Error("后端服务地址未配置，无法删除记录。");
        }

        const response = await fetch(`${BACKEND_BASE_URL}/records/${recordId}`, {
            method: 'DELETE',
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
        }

        setRecords(prevRecords => prevRecords.filter(record => record.id !== recordId));

        if (editingRecord && editingRecord.id === recordId) {
            setEditingRecord(null);
        }

        showNotification('记录删除成功!', 'success');

    } catch (err) {
        console.error('删除记录失败:', err);
        showNotification(`删除记录失败: ${err.message}`, 'error');
    } finally {
        setDeletingId(null);
    }
  };

  const handleStartEditing = (record) => {
    setEditingRecord(record);
  };

  const handleCancelEditing = () => {
    setEditingRecord(null);
  };

  // 处理导入记录的函数
  const handleImportRecords = async (importedRecords) => {
      if (!importedRecords || importedRecords.length === 0) {
          showNotification('没有记录可导入', 'error');
          return;
      }

      // 逐条保存导入的记录
      for (const record of importedRecords) {
          // 调用 handleSaveRecord 来保存到后端
          // 注意：这里假设 importedRecords 中的每条记录结构与 handleSaveRecord 期望的 recordData 结构兼容
          // 特别是 record_time 需要是 Date 对象或能被 new Date() 解析的字符串
          // content 需要是数组或能被处理成数组
          // fee 需要是数字或能被 parseFloat 解析
          try {
              // 格式化导入的记录以匹配 handleSaveRecord 的期望
              const formattedRecord = {
                  ...record,
                  record_time: record.record_time ? new Date(record.record_time) : null,
                  content: Array.isArray(record.content) ? record.content : (record.content ? String(record.content).split(' / ') : []),
                  fee: record.fee !== undefined && record.fee !== null ? parseFloat(record.fee) : null,
                  remarks: record.remarks || null,
                  // 导入的记录通常没有 ID，handleSaveRecord 会作为新记录处理
                  id: null,
              };
              await handleSaveRecord(formattedRecord);
          } catch (error) {
              console.error('导入单条记录失败:', record, error);
              showNotification(`导入部分记录失败: ${error.message}`, 'error');
              // 可以选择是否中断导入或继续
          }
      }
      // 导入完成后重新获取所有记录，以确保列表是最新的
      fetchRecords();
      showNotification('记录导入完成!', 'success');
  };


  // Fetch records only if logged in and backend URL is set
  useEffect(() => {
    if (isLoggedIn && BACKEND_BASE_URL) {
       fetchRecords();
    } else if (!isLoggedIn) {
       setLoading(false); // Stop loading if not logged in
       setRecords([]); // Clear records if not logged in
       setError(null); // Clear any previous errors
    } else { // isLoggedIn is true but BACKEND_BASE_URL is not set
       setLoading(false);
       setError("后端服务地址未配置，请检查环境变量。");
    }
  }, [isLoggedIn]); // Depend on isLoggedIn state

  return (
    <BrowserRouter>
      <div className={styles.appContainer}>
        {/* 提示框显示区域 - 保持不变 */}
        {notification.visible && (
          <div style={notification.type === 'error' ? errorNotificationStyle : notificationStyle}>
            {notification.message}
          </div>
        )}

        {/* 根据登录状态条件渲染 */}
        {isLoggedIn ? (
          <>
            {/* 左侧表单区域 - 保持不变，它不依赖于路由 */}
            {BACKEND_BASE_URL ? (
                <div className={styles.formContainer}> {/* Keep formContainer div */}
                    <ClassRecordForm
                        editingRecord={editingRecord}
                        onSave={handleSaveRecord}
                        onCancelEdit={handleCancelEditing}
                        isSubmitting={isSubmitting}
                        showNotification={showNotification} // 传递提示函数
                        allRecords={records} // 将 records 传递给 ClassRecordForm
                        onLogout={handleLogout} // Pass handleLogout as a prop
                    />
                </div>
            ) : (
                <div className={styles.formContainer} style={{ textAlign: 'center', color: 'red' }}>
                    请配置后端服务地址环境变量。
                </div>
            )}

            {/* 右侧内容区域 - 使用 Routes 和 Route */}
            <div className={styles.mainContent}>
              <Routes>
                {/* 首页路由：显示 ClassRecordList */}
                <Route path="/" element={
                  loading ? <p className={styles.loadingMessage}>加载中...</p> :
                  error ? <p className={styles.errorMessage}>{error}</p> :
                  BACKEND_BASE_URL ? (
                     records.length > 0 ? (
                         <ClassRecordList
                            records={records}
                            onEdit={handleStartEditing}
                            onDelete={handleDeleteRecord}
                            onImport={handleImportRecords}
                         />
                     ) : (
                         <p className={styles.noRecordsMessage}>暂无记录。</p>
                     )
                  ) : null // Or handle the case where BACKEND_BASE_URL is not set
                } />

                {/* 统计页面路由 */}
                <Route path="/statistics" element={<StatisticsPage records={records} />} />

                {/* 可以添加其他路由 */}
              </Routes>
            </div>
          </>
        ) : (
          <Login onLogin={handleLoginSuccess} /> // Render Login component if not logged in
        )}
      </div>
    </BrowserRouter>
  );
}

export default App;
